import {
    confirmResetPassword,
    confirmSignIn,
    confirmSignUp,
    fetchAuthSession,
    resendSignUpCode,
    signIn,
    signInWithRedirect,
    signOut,
    updatePassword as updatePasswordCognito,
} from '@aws-amplify/auth';
import { cognitoUserPoolsTokenProvider } from '@aws-amplify/auth/cognito';
import { Amplify } from 'aws-amplify';
import { Hub, sessionStorage as amplifySessionStorage } from 'aws-amplify/utils';
import AWS from 'aws-sdk';
import axios from 'axios';
import { useSnackbar } from 'contexts/snackbar';
import queryString from 'query-string';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import accountService from 'services/account';
import authService from 'services/auth';
import courseService from 'services/course';
import userService from 'services/user';
import { AUTH_STATES, getAuthStateForSignInStep } from 'utils/auth-challenge';
import {
    clearAuthToken,
    clearLocalAuthStorage,
    configureAwsCognitoIdentityStorage,
    getAuthToken,
    setAuthToken,
} from 'utils/auth-storage';
import { ROLES } from 'utils/constants';
import { formatAppUrl } from 'utils/helper';
import { buildAppPath } from 'utils/routing';
import { v4 as uuid } from 'uuid';

export const launchUri = (url) => {
    const windowProxy = window.open(url, '_self');
    if (windowProxy) {
        return Promise.resolve(windowProxy);
    } else {
        return Promise.reject();
    }
};
let platform = process.env.REACT_APP_COGNITO_SUBDOMAIN;
const AWS_IDENTITY_POOL_ID = process.env.REACT_APP_AWS_IDENTITY_POOL_ID;

const getLocalizedBasePath = () => {
    const { pathname } = window.location;

    const parts = pathname.split('/').filter(Boolean);

    // Expect at least: [lang, country, appBase]
    if (parts.length >= 3) {
        return `/${parts[0]}/${parts[1]}/${parts[2]}`;
    }

    // fallback for older non-localized paths
    const fallbackBase = process.env.REACT_APP_BASE_NAME || '';
    return fallbackBase.startsWith('/') ? fallbackBase : `/${fallbackBase}`;
};

const isForgotPasswordPath = () => {
    return window.location.pathname.endsWith('/forgot-password');
};

const decodeAuthToken = (token) => {
    try {
        const payload = token.split('.')[1];
        const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/');
        const paddedPayload = normalizedPayload.padEnd(
            normalizedPayload.length + ((4 - (normalizedPayload.length % 4)) % 4),
            '='
        );
        const decodedPayload = decodeURIComponent(
            window
                .atob(paddedPayload)
                .split('')
                .map((char) => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
                .join('')
        );
        return JSON.parse(decodedPayload);
    } catch {
        return null;
    }
};

const configureAmplifyAuth = () => {
    const origin = window.location.hostname === 'localhost' ? 'http://localhost:8000' : window.location.origin;

    const basename = getLocalizedBasePath();
    const redirectSignIn = `${origin}${basename}/federated-login`;
    const redirectSignOut = `${origin}${basename}`;

    const baseConfig = {
        Auth: {
            region: process.env.REACT_APP_AWS_REGION,
            Cognito: {
                userPoolId: process.env.REACT_APP_USERPOOL_ID,
                userPoolClientId: process.env.REACT_APP_CLIENT_ID,
                mandatorySignIn: false,
            },
        },
    };
    const configureAuth = (authConfig) => {
        clearLocalAuthStorage();
        cognitoUserPoolsTokenProvider.setAuthConfig(authConfig);
        cognitoUserPoolsTokenProvider.setKeyValueStorage(amplifySessionStorage);
        Amplify.configure({ Auth: authConfig }, { Auth: { tokenProvider: cognitoUserPoolsTokenProvider } });
    };

    if (isForgotPasswordPath()) {
        configureAuth(baseConfig.Auth);
        return;
    }

    configureAuth({
        ...baseConfig.Auth,
        Cognito: {
            ...baseConfig.Auth.Cognito,
            loginWith: {
                oauth: {
                    domain: process.env.REACT_APP_COGNITO_DOMAIN,
                    scopes: ['email', 'profile', 'openid', 'aws.cognito.signin.user.admin'],
                    redirectSignIn: [redirectSignIn],
                    redirectSignOut: [redirectSignOut],
                    responseType: 'code',
                },
            },
        },
    });
};

const AuthContext = createContext();
const useAuth = () => useContext(AuthContext);

export { AUTH_STATES };
export { useAuth };

const getSessionIdentity = (session) => {
    const accessToken = session?.tokens?.accessToken;
    return {
        accessToken: accessToken?.toString(),
        userSub: accessToken?.payload?.sub,
    };
};

const AuthProvider = (props) => {
    const { message } = useSnackbar();
    const history = useHistory();
    const location = useLocation();
    const { t, i18n } = useTranslation('assignments');

    const [auth, setAuth] = useState();
    const [currentUser, setCurrentUser] = useState();
    const [loading, setLoading] = useState(true);

    let cachedCredentials = useRef(null);
    const tokenCache = useRef(new Map()); // Cache to store tokens per account
    const loginRequest = useRef(null);

    const updateCurrentUser = async (data) => {
        const updateUser = await userService.updateUser(auth.user_id, data);
        setCurrentUser(updateUser);
        return updateUser;
    };

    const logout = useCallback(
        async ({ isExpired = false } = {}) => {
            const token = getAuthToken();
            if (token) {
                axios.defaults.headers.common.Authorization = `Bearer ${token}`;
                try {
                    await authService.logout();
                } catch (e) {
                    // Local logout should still complete if revocation fails because the token is already invalid.
                    console.error(e);
                }
            }
            clearAuthToken();
            axios.defaults.headers.common.Authorization = undefined;
            const redirectPath = isExpired ? '/session-expired' : '/';
            const redirectUrl = `${window.location.origin}${buildAppPath(redirectPath)}`;
            await signOut({
                global: false,
                oauth: { redirectUrl },
            });
            setAuth(undefined);
            if (isExpired) {
                setLoading(false);
                return history.replace('/session-expired');
            }
            return history.push('/');
        },
        [history]
    );

    const verifyUser = useCallback(async (username, code) => {
        try {
            await confirmSignUp({ username, confirmationCode: code });
            return { success: true };
        } catch (e) {
            if (e?.name === 'NotAuthorizedException' && e?.message?.includes('CONFIRMED')) {
                return { success: true, alreadyConfirmed: true };
            }
            throw e;
        }
    }, []);

    const finishLogin = useCallback(async (username) => {
        const session = await fetchAuthSession();
        const { accessToken, userSub } = getSessionIdentity(session);
        if (!accessToken || !userSub) throw new Error('Cognito authentication did not return a complete session.');

        const res = await authService.signIn({ access_token: accessToken });
        if (!res?.auth_token) throw new Error('TimelyGrader authentication failed.');
        const { auth_token: token } = res;
        setAuthToken(token);
        axios.defaults.headers.common.Authorization = `Bearer ${token}`;
        const cachedUser = decodeAuthToken(token);
        const userData = await userService.getUser(cachedUser.user_id);
        if (cachedUser.owner_account_id) {
            const accountData = await accountService.findAccount(cachedUser.owner_account_id);
            setAuth({ ...cachedUser, ...accountData, cognito_user_id: userSub });
        } else {
            setAuth({ ...cachedUser, cognito_user_id: userSub });
        }
        setCurrentUser({ ...res, ...userData, username, cognito_user_id: userSub });
        return !userData?.profile_set_up ? 'profile-set-up' : 'login';
    }, []);

    const login = useCallback(
        async ({ username = '', password = '' }) => {
            if (loginRequest.current) return loginRequest.current;

            const request = (async () => {
                const trimmedUsername = username?.toLowerCase()?.trim();
                const existingSession = await fetchAuthSession();
                const existingAccessToken = existingSession?.tokens?.accessToken;
                const existingEmail = existingSession?.tokens?.idToken?.payload?.email?.toLowerCase()?.trim();

                if (existingAccessToken) {
                    if (existingEmail === trimmedUsername) {
                        return {
                            state: AUTH_STATES.AUTHENTICATED,
                            route: await finishLogin(trimmedUsername),
                        };
                    }

                    await signOut({ global: false });
                    clearAuthToken();
                    axios.defaults.headers.common.Authorization = undefined;
                }

                let signInResult;
                try {
                    signInResult = await signIn({ username: trimmedUsername, password });
                } catch (error) {
                    if (error?.name !== 'UserAlreadyAuthenticatedException') throw error;

                    const recoveredSession = await fetchAuthSession();
                    if (!recoveredSession?.tokens?.accessToken) throw error;
                    const recoveredEmail = recoveredSession.tokens.idToken?.payload?.email?.toLowerCase()?.trim();

                    if (recoveredEmail === trimmedUsername) {
                        return {
                            state: AUTH_STATES.AUTHENTICATED,
                            route: await finishLogin(trimmedUsername),
                        };
                    }

                    await signOut({ global: false });
                    clearAuthToken();
                    axios.defaults.headers.common.Authorization = undefined;
                    signInResult = await signIn({ username: trimmedUsername, password });
                }

                const { nextStep, isSignedIn } = signInResult;
                const authState = getAuthStateForSignInStep(nextStep?.signInStep);
                switch (authState) {
                    case AUTH_STATES.CONFIRM_SIGN_UP_REQUIRED:
                        return { state: AUTH_STATES.CONFIRM_SIGN_UP_REQUIRED, email: trimmedUsername };
                    case AUTH_STATES.NEW_PASSWORD_REQUIRED:
                        return { state: AUTH_STATES.NEW_PASSWORD_REQUIRED, email: trimmedUsername };
                    case AUTH_STATES.PASSWORD_RESET_REQUIRED:
                        return { state: AUTH_STATES.PASSWORD_RESET_REQUIRED, email: trimmedUsername };
                    case AUTH_STATES.AUTHENTICATED:
                        if (isSignedIn) {
                            return {
                                state: AUTH_STATES.AUTHENTICATED,
                                route: await finishLogin(trimmedUsername),
                            };
                        }
                        break;
                    default:
                        break;
                }

                throw new Error(`Unsupported Cognito sign-in challenge: ${nextStep?.signInStep || 'unknown'}`);
            })();

            loginRequest.current = request;
            try {
                return await request;
            } finally {
                if (loginRequest.current === request) loginRequest.current = null;
            }
        },
        [finishLogin]
    );

    const completeNewPassword = useCallback(
        async (email, newPassword) => {
            const { nextStep, isSignedIn } = await confirmSignIn({ challengeResponse: newPassword });
            if (!isSignedIn || nextStep?.signInStep !== 'DONE') {
                throw new Error(`Unsupported Cognito sign-in challenge: ${nextStep?.signInStep || 'unknown'}`);
            }
            return {
                state: AUTH_STATES.AUTHENTICATED,
                route: await finishLogin(email?.toLowerCase()?.trim()),
            };
        },
        [finishLogin]
    );

    const loginWithThirdParty = useCallback(
        async (email, cognitoUserId) => {
            try {
                setLoading(true);
                const session = await fetchAuthSession();
                const { accessToken } = getSessionIdentity(session);
                const res = await authService.signIn({ access_token: accessToken });
                const { auth_token: token } = res;
                if (!res) return new Error();
                setAuthToken(token);
                axios.defaults.headers.common.Authorization = `Bearer ${token}`;
                const cachedUser = decodeAuthToken(token);
                const userData = await userService.getUser(cachedUser.user_id);
                const user = await fetchAuthSession();
                const { userSub } = getSessionIdentity(user);

                if (cachedUser.owner_account_id) {
                    const accountData = await accountService.findAccount(cachedUser.owner_account_id);
                    setAuth({
                        ...cachedUser,
                        ...userData,
                        ...accountData,
                        cognito_user_id: userSub,
                        is_third_party: true,
                    });
                } else {
                    setAuth({ ...cachedUser, ...userData, is_third_party: true, cognito_user_id: userSub });
                }
                setCurrentUser({ ...res, ...userData, is_third_party: true, cognito_user_id: userSub });
                if (!res?.profile_set_up && user.tokens.idToken.payload['identities']?.[0]?.providerName !== 'google') {
                    return history.push('/account-settings');
                } else if (!res?.profile_set_up) {
                    return history.push('/profile-setup');
                } else {
                    setTimeout(() => {
                        history.push('/');
                    }, 2000);
                }
            } catch (e) {
                console.log(e);
                throw new Error();
            }
        },
        [history]
    );

    const sessionExpired = useCallback(() => {
        return logout({ isExpired: true });
    }, [logout]);

    const selectAccount = async (accountId) => {
        setLoading(true);
        const res = await authService.selectAccount({ owner_account_id: accountId });
        const { auth_token: token } = res;
        setAuthToken(token);
        axios.defaults.headers.common.Authorization = `Bearer ${token}`;
        const cachedUser = decodeAuthToken(token);
        const accountData = await accountService.findAccount(cachedUser.owner_account_id);
        const courseData = await courseService.findCourse(accountData.course_id);
        const isThirtdParty = await getCurrentUserStatus();
        setAuth({ ...cachedUser, ...accountData, is_third_party: isThirtdParty });
        if (
            !courseData?.finished_course_set_up &&
            accountData?.role !== 'student' &&
            courseData?.created_on > new Date('2021-12-01').getTime()
        ) {
            history.push('/course-set-up');
        } else if (accountData?.role === 'instructor' || accountData?.role === 'student') {
            history.push('/assignments');
        } else {
            history.push('/dashboard');
        }
        setLoading(false);
    };

    const startCourseSetup = async (originalCourseObj) => {
        setLoading(true);
        setAuth((current) => {
            return { iat: current.iat, user_id: current.user_id, username: current.username };
        });

        const newCourseId = uuid();
        let newCourseData = {
            id: newCourseId,
            name: '',
        };
        if (originalCourseObj) {
            const { course_id, name } = originalCourseObj;
            newCourseData = {
                ...newCourseData,
                name: t('duplicate_title', 'Copy of {{title}}', { title: name }),
                original_course_id: course_id,
            };
            history.push({
                pathname: '/course-set-up',
                state: { duplicatingCourse: newCourseData },
            });
        } else {
            history.push('/course-set-up');
        }

        setLoading(false);
        return newCourseData;
    };

    const confirmCourseSetUp = async (course) => {
        const accountId = uuid();
        await accountService.createAccount(accountId, {
            course_id: course.id,
            user_data: { user_id: auth.user_id, role: ROLES.INSTRUCTOR },
        });
        await courseService.createCourse(course.id, course);
    };

    const resendConfirmationCode = async (username) => {
        try {
            await resendSignUpCode({ username });
            message.success(
                t('common:message.resend-confirmation-code', 'The verification email was re-sent successfully.')
            );
        } catch (err) {
            console.log('error resending code: ', err);
        }
    };

    const updatePassword = async (oldPassword, newPassword) => {
        return await fetchAuthSession()
            .then(async () => {
                if (!newPassword) return new Error();
                await updatePasswordCognito({
                    oldPassword: oldPassword || currentUser.password,
                    newPassword,
                });
                return true;
            })
            .catch((e) => {
                console.log(e);
                return false;
            });
    };

    const register = async ({ username, password, firstName = '', lastName = '' }) => {
        try {
            const basename = process.env.REACT_APP_BASE_NAME;
            const trimmedUsername = username?.toLowerCase()?.trim();
            const trimmedFirstName = firstName?.trim();
            const trimmedLastName = lastName?.trim();
            const fullName = [trimmedFirstName, trimmedLastName].filter(Boolean).join(' ');
            const signUpData = {
                display_name: trimmedFirstName,
                name: fullName,
                username: trimmedUsername,
                email: trimmedUsername,
                password,
                last_name: trimmedLastName,
                first_name: trimmedFirstName,
                admin: false,
                base_url: formatAppUrl(i18n.language, basename),
            };
            const createUser = await authService.signUp(signUpData);
            message.success(t('common:message.sign-up-success', 'The verification email was sent successfully.'));
            return createUser;
        } catch (e) {
            return e?.data || e?.name;
        }
    };

    const forgotPassword = async (email) => {
        try {
            const findUser = await authService.forgotPassword({ username: email });
            return findUser;
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
        }
    };

    const confirmForgotPassword = async (email, code, newPassword) => {
        return confirmResetPassword({
            username: email?.toLowerCase()?.trim(),
            newPassword,
            confirmationCode: code,
        });
    };

    const getCurrentUser = () => currentUser || null;

    const updateNotificationPreferences = async (newNotification) => {
        try {
            setAuth((auth) => ({
                ...auth,
                notification: newNotification,
            }));
            await accountService.updateAccount(auth.owner_account_id, { notification: newNotification });
        } catch (e) {
            console.error(e);
        }
    };

    const updateProfile = async (data) => {
        const prevUser = { ...currentUser };
        const userId = auth?.user_id || currentUser?.id;
        if (!userId) {
            console.error('Profile update requires an authenticated user.');
            return null;
        }

        const updatedUser = {
            ...currentUser,
            ...data,
        };
        setCurrentUser(updatedUser);

        try {
            const persistedData = { ...data };
            if (persistedData.profile_image?.preview_url) {
                persistedData.profile_image = { ...persistedData.profile_image };
                delete persistedData.profile_image.preview_url;
            }
            await userService.updateUser(userId, persistedData);
            return updatedUser;
        } catch (e) {
            setCurrentUser(prevUser);
            console.error('Failed to update profile.', e);
            return null;
        }
    };

    const changeAccount = async () => {
        setLoading(true);
        const res = await authService.unselectAccount();
        const { auth_token: token } = res;
        setAuthToken(token);
        axios.defaults.headers.common.Authorization = `Bearer ${token}`;
        setAuth((current) => ({ iat: current.iat, user_id: current.user_id, username: current.username }));
        setLoading(false);
        return history.push('/');
    };

    const signInWithGoogle = async () => {
        try {
            setLoading(true);
            await signInWithRedirect({
                provider: 'Google',
            });
        } catch (e) {
            console.log(e);
        }
    };

    const getCurrentUserStatus = async () => {
        const session = await fetchAuthSession();
        if (session?.idToken?.payload?.['cognito:username']?.includes('google')) {
            return true;
        }
        return false;
    };

    const getAccount = async () => {
        if (!auth) return;
        return await accountService.findAccount(auth.owner_account_id);
    };

    const getCachedAccountToken = useCallback(
        async (ownerAccountId) => {
            const cachedToken = tokenCache.current.get(ownerAccountId);

            if (cachedToken && cachedToken.expiresAt > Date.now()) {
                return cachedToken;
            }

            const accountTokens = await accountService.getAccountTokens(ownerAccountId);
            const tokenExpiryTime = Date.now() + 55 * 60 * 1000; // 55 minutes

            tokenCache.current.set(ownerAccountId, {
                IdentityId: accountTokens.IdentityId,
                Token: accountTokens.Token,
                expiresAt: tokenExpiryTime,
            });

            return accountTokens;
        },
        [tokenCache]
    );

    useEffect(() => {
        const unsubscribe = Hub.listen('auth', async ({ payload: { event, data } }) => {
            switch (event) {
                case 'signInWithRedirect': {
                    break;
                }
                case 'signedIn': {
                    setLoading(true);
                    try {
                        const user = await fetchAuthSession();
                        const provider = user.tokens?.idToken?.payload?.['identities']?.[0]?.providerName || 'Cognito';
                        const { email } = user.tokens.idToken.payload;
                        if (provider.toLowerCase() === 'google') {
                            await loginWithThirdParty(email, data?.username);
                        }
                    } catch (error) {
                        console.error('Federated sign-in failed:', error);
                    } finally {
                        setLoading(false);
                    }
                    break;
                }
                case 'signOut':
                    break;
                case 'customOAuthState':
                    break;
                case 'signInWithRedirect_failure':
                    setLoading(false);
                    break;
                default:
                    break;
            }
        });

        return unsubscribe;
    }, [loginWithThirdParty]);

    useEffect(() => {
        if (!auth?.owner_account_id) return;

        let intervalId;
        let running = false; // prevent overlap
        let sessionExpiryTriggered = false;
        const REFRESH_MS = 30 * 60 * 1000;

        const getAwsCredentials = async (accountId) => {
            configureAwsCognitoIdentityStorage(AWS);

            if (cachedCredentials.current && !cachedCredentials.current.needsRefresh()) {
                return cachedCredentials.current;
            }

            const accountTokens = await getCachedAccountToken(accountId);
            cachedCredentials.current = new AWS.CognitoIdentityCredentials({
                IdentityPoolId: AWS_IDENTITY_POOL_ID,
                IdentityId: accountTokens.IdentityId,
                Logins: { 'cognito-identity.amazonaws.com': accountTokens.Token },
            });

            await cachedCredentials.current.getPromise(); // Ensure credentials are ready
            return cachedCredentials.current;
        };

        const updateAWSConfig = async () => {
            if (running) return;
            running = true;
            try {
                AWS.config.update({ region: process.env.REACT_APP_AWS_REGION });

                const c = await getAwsCredentials(auth.owner_account_id);
                AWS.config.update({
                    credentials: new AWS.Credentials(c.accessKeyId, c.secretAccessKey, c.sessionToken),
                });
            } catch (e) {
                console.error('Failed to refresh AWS creds:', e);
                if (!sessionExpiryTriggered) {
                    sessionExpiryTriggered = true;
                    clearInterval(intervalId);
                    await sessionExpired().catch((logoutError) => {
                        console.error('Failed to expire session after AWS credential refresh failure:', logoutError);
                    });
                }
            } finally {
                running = false;
            }
        };

        // initial fetch
        updateAWSConfig();

        // refresh every 30 minutes
        intervalId = setInterval(() => {
            updateAWSConfig().catch(console.error);
        }, REFRESH_MS);

        return () => clearInterval(intervalId);
    }, [auth?.owner_account_id, getCachedAccountToken, sessionExpired]);

    useEffect(() => {
        configureAmplifyAuth();
    }, []);

    useEffect(() => {
        const getCurrentSession = async () => {
            let cachedUser = null;

            let token = getAuthToken();
            if (auth && location.pathname === '/lti-launch') {
                const { token: ltiLaunchToken } = queryString.parse(location.search);
                clearAuthToken();
                if (ltiLaunchToken) {
                    setAuthToken(ltiLaunchToken);
                    token = ltiLaunchToken;
                }
            }
            if (location.pathname === '/accepted-invitation') {
                try {
                    const basename = getLocalizedBasePath();
                    await signOut({
                        global: false,
                        oauth: {
                            redirects: `https://${platform}.timelygrader.ai${basename}`,
                            redirectUrl: `https://${platform}.timelygrader.ai${basename}`,
                        },
                    });
                } catch (e) {
                    console.log('Ignoring signOut error on accepted-invitation:', e);
                }

                clearAuthToken();
                axios.defaults.headers.common.Authorization = undefined;

                const { token: incomingToken, confirmation_code } = queryString.parse(location.search);

                setAuthToken(incomingToken);
                cachedUser = decodeAuthToken(incomingToken);
                axios.defaults.headers.common.Authorization = `Bearer ${incomingToken}`;
                try {
                    const userData = await userService.getUser(cachedUser.user_id);
                    await verifyUser(userData.username, confirmation_code);

                    if (userData?.finished_sign_up || userData?.profile_set_up) {
                        setLoading(false);
                        message.success(
                            t(
                                'common:message.accepted-invitation',
                                'Thank you for accepting the invitation. Please login again.'
                            )
                        );
                        return logout({ isExpired: false });
                    }
                    setAuth({ ...cachedUser });
                    setCurrentUser(userData);

                    const result = await login({
                        username: userData.username,
                        password: userData.password,
                    });
                    if (result.state === AUTH_STATES.AUTHENTICATED && result.route === 'profile-set-up') {
                        history.push('/account-settings');
                    }
                } catch (e) {
                    setLoading(false);
                    console.log(e);
                    return logout({ isExpired: false });
                }
            } else if (token) {
                cachedUser = decodeAuthToken(token);
                axios.defaults.headers.common.Authorization = `Bearer ${token}`;
                if (cachedUser?.owner_account_id) {
                    const accountData = await accountService.findAccount(cachedUser.owner_account_id);
                    if (accountData?.active === false) return await logout({ isExpired: true });
                    setAuth({ ...cachedUser, ...accountData });
                    const userData = await userService.getUser(cachedUser.user_id);
                    const userStatus = await getCurrentUserStatus();
                    setCurrentUser(userData);

                    if (accountData?.course_id) {
                        const existingCourseData = await courseService.findCourse(accountData?.course_id);

                        if (
                            cachedUser.role === 'instructor' &&
                            !existingCourseData?.finished_course_set_up &&
                            existingCourseData?.created_on > new Date('2021-12-01').getTime()
                        ) {
                            history.push('/course-set-up');
                        }
                        if (userData && cachedUser?.role && cachedUser.role !== 'admin' && !userData.profile_set_up) {
                            if (!userStatus) {
                                history.push('/account-settings');
                            } else {
                                history.push('/profile-setup');
                            }
                        }
                    }
                } else {
                    const userData = await userService.getUser(cachedUser.user_id);
                    const userStatus = await getCurrentUserStatus();
                    setCurrentUser(userData);
                    setAuth({ ...cachedUser, ...userData });
                    if (userData && !userData.profile_set_up) {
                        if (!userStatus) {
                            history.push('/account-settings');
                        } else {
                            history.push('/profile-setup');
                        }
                    }
                }
                try {
                    const session = await fetchAuthSession();
                    if (session?.idToken?.payload?.['cognito:username']?.includes('google')) {
                        setAuth((current) => ({ ...current, is_third_party: true }));
                        setCurrentUser((current) => ({ ...current, is_third_party: true }));
                    }
                    setLoading(false);
                } catch (e) {
                    // eslint-disable-next-line no-console
                    console.error(e);
                    return logout({ isExpired: true });
                }
            } else {
                setAuth(null);
                setLoading(false);
            }
        };

        if (!auth) {
            getCurrentSession();
        }
    }, [auth, history, location.pathname, location.search, login, logout, message, verifyUser, t]);

    return (
        <AuthContext.Provider
            value={{
                loading,
                auth,
                currentUser,
                setAuth,
                getCurrentUser,
                setCurrentUser,
                updateCurrentUser,
                updateNotificationPreferences,
                updateProfile,
                login,
                loginWithThirdParty,
                completeNewPassword,
                logout,
                forgotPassword,
                updatePassword,
                selectAccount,
                changeAccount,
                confirmForgotPassword,
                resendConfirmationCode,
                register,
                verifyUser,
                sessionExpired,
                startCourseSetup,
                confirmCourseSetUp,
                signInWithGoogle,
                getAccount,
            }}
            {...props}
        />
    );
};

export default AuthProvider;
